import { useId, useState } from 'react';
import type { MouseEvent } from 'react';
import Link from 'next/link';
import ProgressBar from '@ramonak/react-progress-bar';
import dayjs from 'dayjs';
import classNames from 'classnames';
import { theme } from 'tailwind.config';

import { POLL_CLOSED_ERROR_CODE } from '@api-service/captaincy-poll';
import {
  useCaptaincyPollDefinition,
  useCaptaincyPollResults
} from '@hooks/useCaptaincyPoll';
import { useLoginLink } from '@hooks/useLoginLink';
import { AnalyticsEvents, captureAnalyticsEvent } from '@lib/analytics/events';
import {
  DEFAULT_CAPTAINCY_POLL_QUESTION,
  toVoteShare
} from '@lib/captaincy-poll';
import type { CaptaincyPollStatus } from '@lib/captaincy-poll';
import type {
  CaptaincyPoll as CaptaincyPollDefinition,
  CaptaincyPollPlayer
} from '@lib/sanity/captaincyPoll';

export type CaptaincyPollPlacement = 'sidebar' | 'mobile_modal';

const DEADLINE_FORMAT = 'ddd D MMM, HH:mm';

const playerLabel = ({ displayName, teamShortName }: CaptaincyPollPlayer) =>
  teamShortName ? `${displayName} (${teamShortName})` : displayName;

const voteErrorMessage = (error: Error) =>
  error.message === POLL_CLOSED_ERROR_CODE
    ? 'Voting has closed for this gameweek.'
    : "We couldn't save your vote. Please try again.";

const votesLabel = (count: number) =>
  `${count} ${count === 1 ? 'vote' : 'votes'}`;

type ResultsListProperties = {
  players: CaptaincyPollPlayer[];
  votesByPlayer: Map<string, number>;
  totalVotes: number;
  myVote: string | null;
};

const ResultsList = ({
  players,
  votesByPlayer,
  totalVotes,
  myVote
}: ResultsListProperties) => (
  <ul className="m-0 mt-4 list-none space-y-3 p-0">
    {players.map((player) => {
      const share = toVoteShare(
        votesByPlayer.get(player.optaUuid) ?? 0,
        totalVotes
      );
      const isMyVote = player.optaUuid === myVote;
      return (
        <li key={player.optaUuid}>
          <div className="flex items-baseline justify-between gap-2 font-open-sans text-sm text-neutral-800">
            <span className={classNames({ 'font-semibold': isMyVote })}>
              {playerLabel(player)}
              {isMyVote && (
                <span className="ml-2 text-xs font-semibold text-primary-700">
                  Your pick
                </span>
              )}
            </span>
            <span className="font-semibold">{share}%</span>
          </div>
          {/* The share is written out above, so the bar is decoration. */}
          <div aria-hidden="true" className="mt-1.5">
            <ProgressBar
              bgColor={theme.colors.primary[isMyVote ? 700 : 600]}
              baseBgColor={theme.colors.neutral[300]}
              height="6px"
              completed={share}
              isLabelVisible={false}
              animateOnRender
            />
          </div>
        </li>
      );
    })}
  </ul>
);

type OptionsListProperties = {
  players: CaptaincyPollPlayer[];
  myVote: string | null;
  isDisabled: boolean;
  onVote: (optaUuid: string) => void;
};

const OptionsList = ({
  players,
  myVote,
  isDisabled,
  onVote
}: OptionsListProperties) => (
  <ul className="m-0 mt-4 list-none space-y-2 p-0">
    {players.map((player) => {
      const isMyVote = player.optaUuid === myVote;
      return (
        <li key={player.optaUuid}>
          <button
            type="button"
            disabled={isDisabled}
            aria-pressed={isMyVote}
            onClick={() => onVote(player.optaUuid)}
            className={classNames(
              'flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left font-open-sans text-sm font-semibold text-neutral-800 transition-colors',
              'disabled:cursor-not-allowed disabled:opacity-60',
              isMyVote
                ? 'border-primary-600 bg-primary-50'
                : 'border-neutral-300 bg-white hover:border-primary-400 hover:bg-neutral-100'
            )}
          >
            {playerLabel(player)}
            {isMyVote && (
              <span className="text-xs text-primary-700">Your pick</span>
            )}
          </button>
        </li>
      );
    })}
  </ul>
);

const PlayerList = ({ players }: { players: CaptaincyPollPlayer[] }) => (
  <ul className="m-0 mt-4 list-none space-y-2 p-0">
    {players.map((player) => (
      <li
        key={player.optaUuid}
        className="rounded-lg border border-neutral-300 px-3 py-2.5 font-open-sans text-sm font-semibold text-neutral-800"
      >
        {playerLabel(player)}
      </li>
    ))}
  </ul>
);

type CaptaincyPollCardProperties = {
  poll: CaptaincyPollDefinition;
  status: Exclude<CaptaincyPollStatus, 'hidden'>;
  placement: CaptaincyPollPlacement;
  className?: string;
};

const CaptaincyPollCard = ({
  poll,
  status,
  placement,
  className
}: CaptaincyPollCardProperties) => {
  const headingId = useId();
  const loginLink = useLoginLink();
  const [isChangingVote, setIsChangingVote] = useState(false);
  const {
    results,
    isLoading,
    isError,
    isLoggedIn,
    vote,
    isVoting,
    voteError,
    resetVoteError
  } = useCaptaincyPollResults(poll.gameweek);

  const isOpen = status === 'open' && results?.isOpen !== false;
  const myVote = results?.myVote ?? null;
  const votesByPlayer = new Map(
    (results?.tallies ?? []).map(({ playerOptaId, votes }) => [
      playerOptaId,
      votes
    ])
  );
  // Only the players shown count, so the bars add up even when an editor has
  // taken a player out after people voted for them.
  const totalVotes = poll.players.reduce(
    (total, player) => total + (votesByPlayer.get(player.optaUuid) ?? 0),
    0
  );
  const shouldShowResults = !isOpen || (myVote !== null && !isChangingVote);

  const handleVote = async (playerOptaId: string) => {
    if (playerOptaId === myVote) {
      setIsChangingVote(false);
      return;
    }
    resetVoteError();
    try {
      await vote(playerOptaId);
      setIsChangingVote(false);
      captureAnalyticsEvent(AnalyticsEvents.captaincyPollVoted, {
        placement,
        is_change: myVote !== null
      });
    } catch {
      // The error is shown from the mutation's state.
    }
  };

  const handleChangeVote = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    resetVoteError();
    setIsChangingVote(true);
  };

  const subtitle = isOpen
    ? `Voting closes ${dayjs(results?.closesAt ?? undefined).format(
        DEADLINE_FORMAT
      )}`
    : `Voting closed · ${votesLabel(totalVotes)}`;

  return (
    <section
      aria-labelledby={headingId}
      className={classNames(
        'rounded-[12px] border border-neutral-200 bg-white p-4 shadow',
        className
      )}
    >
      <p className="m-0 font-open-sans text-xs font-semibold uppercase tracking-wide text-primary-700">
        Captaincy poll
      </p>
      <h4
        id={headingId}
        className="m-0 mt-1 font-sans text-[17px] font-semibold leading-6 text-neutral-800"
      >
        {poll.question ?? DEFAULT_CAPTAINCY_POLL_QUESTION(poll.gameweek)}
      </h4>
      {results && (
        <p className="m-0 mt-1 font-open-sans text-xs text-neutral-600">
          {subtitle}
        </p>
      )}

      {shouldShowResults && results && (
        <ResultsList
          players={poll.players}
          votesByPlayer={votesByPlayer}
          totalVotes={totalVotes}
          myVote={myVote}
        />
      )}
      {shouldShowResults && !results && isError && (
        <p className="m-0 mt-4 font-open-sans text-sm text-neutral-600">
          The results are unavailable right now.
        </p>
      )}
      {!shouldShowResults && isLoggedIn && (
        <OptionsList
          players={poll.players}
          myVote={myVote}
          isDisabled={isVoting || isLoading}
          onVote={handleVote}
        />
      )}
      {!shouldShowResults && !isLoggedIn && (
        <PlayerList players={poll.players} />
      )}

      {voteError && (
        <p
          role="alert"
          className="m-0 mt-3 font-open-sans text-sm text-danger-600"
        >
          {voteErrorMessage(voteError)}
        </p>
      )}
      {isOpen && !isLoggedIn && (
        <p className="m-0 mt-4 font-open-sans text-sm text-neutral-800">
          <Link
            prefetch={false}
            {...loginLink}
            className="font-semibold text-primary-700 hover:underline"
          >
            Log in to vote
          </Link>{' '}
          and see what everyone else picked.
        </p>
      )}
      {isOpen && shouldShowResults && (
        <div className="mt-4 flex items-center justify-between font-open-sans text-xs text-neutral-600">
          <span>{votesLabel(totalVotes)} so far</span>
          <button
            type="button"
            onClick={handleChangeVote}
            className="font-semibold text-primary-700 hover:underline"
          >
            Change my vote
          </button>
        </div>
      )}
      {isOpen && isChangingVote && myVote !== null && (
        <div className="mt-4 text-right font-open-sans text-xs">
          <button
            type="button"
            onClick={() => setIsChangingVote(false)}
            className="font-semibold text-primary-700 hover:underline"
          >
            Keep my vote
          </button>
        </div>
      )}
    </section>
  );
};

type CaptaincyPollProperties = {
  placement: CaptaincyPollPlacement;
  className?: string;
};

/**
 * The weekly captaincy poll: the players the editors picked in Sanity, with
 * the votes from ffh-backend. Renders nothing when there is no poll for the
 * current or next gameweek.
 */
const CaptaincyPoll = ({ placement, className }: CaptaincyPollProperties) => {
  const { poll, status } = useCaptaincyPollDefinition();
  if (!poll || status === 'hidden') return null;

  return (
    <CaptaincyPollCard
      poll={poll}
      status={status}
      placement={placement}
      className={className}
    />
  );
};

export default CaptaincyPoll;
